import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Timer, X } from 'lucide-react';

// Default questions for backward compatibility
const defaultQuestions = [
  {
    id: 1,
    question: "What's the correct way to say 'Hello' in English?",
    options: ["Goodbye", "Thank you", "Hello", "Please"],
    correctAnswer: "Hello",
    emoji: "👋"
  },
  {
    id: 2,
    question: "Which phrase means 'Thank you' in English?",
    options: ["Hello", "Thank you", "Goodbye", "Sorry"],
    correctAnswer: "Thank you",
    emoji: "🙏"
  },
  {
    id: 3,
    question: "How do you say 'Good morning' in English?",
    options: ["Good night", "Good evening", "Good afternoon", "Good morning"],
    correctAnswer: "Good morning",
    emoji: "🌅"
  },
  {
    id: 4,
    question: "Which is the correct way to say 'Yes'?",
    options: ["No", "Maybe", "Yes", "Sorry"],
    correctAnswer: "Yes",
    emoji: "✅"
  },
  {
    id: 5,
    question: "What's the correct way to say 'Goodbye'?",
    options: ["Hello", "Thank you", "Please", "Goodbye"],
    correctAnswer: "Goodbye",
    emoji: "👋"
  }
];

const QuizSection = ({ questions: propQuestions, onComplete }) => {
  const questions = propQuestions || defaultQuestions;
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showScore, setShowScore] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(true);

  useEffect(() => {
    if (timeLeft > 0 && timerActive) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && timerActive) {
      handleAnswerSubmit(null);
    }
  }, [timeLeft, timerActive]);

  const handleAnswerSelect = (answer) => {
    if (selectedAnswer === null) {
      setSelectedAnswer(answer);
    }
  };

  const handleAnswerSubmit = (answer) => {
    setTimerActive(false);
    const currentQ = questions[currentQuestion];
    if (answer === currentQ.correctAnswer) {
      setScore(score + 1);
      setIsCorrect(true);
    } else {
      setIsCorrect(false);
    }
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
        setIsCorrect(null);
        setTimeLeft(30);
        setTimerActive(true);
      } else {
        setShowScore(true);
      }
    }, 2000);
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setScore(0);
    setShowScore(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setTimeLeft(30);
    setTimerActive(true);
  };

  useEffect(() => {
    if (showScore && onComplete) {
      // Call onComplete after a short delay to show the score
      const timer = setTimeout(() => {
        onComplete();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showScore, onComplete]);

  if (showScore) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Quiz Complete! 🎉</h2>
        <p className="text-xl mb-6">
          You scored {score} out of {questions.length}
        </p>
        <div className="flex justify-center space-x-4">
          <button
            onClick={restartQuiz}
            className="bg-purple-600 text-white px-6 py-3 rounded-md font-medium hover:bg-purple-700 transition-colors duration-200"
          >
            Try Again
          </button>
          {onComplete && (
            <button
              onClick={onComplete}
              className="bg-gray-600 text-white px-6 py-3 rounded-md font-medium hover:bg-gray-700 transition-colors duration-200"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="flex justify-between items-center mb-6">
        <div className="text-lg font-semibold">
          Question {currentQuestion + 1}/{questions.length}
        </div>
        <div className="flex items-center text-gray-600">
          <Timer className="w-5 h-5 mr-2" />
          <span className={timeLeft <= 10 ? 'text-red-500' : ''}>
            {timeLeft}s
          </span>
        </div>
      </div>

      <div className="mb-8 text-center">
        <span className="text-4xl mb-4 block">
          {questions[currentQuestion].emoji}
        </span>
        <h2 className="text-xl font-semibold">
          {questions[currentQuestion].question}
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {questions[currentQuestion].options.map((option, index) => (
          <button
            key={index}
            onClick={() => {
              if (selectedAnswer === null) {
                handleAnswerSelect(option);
                handleAnswerSubmit(option);
              }
            }}
            className={`p-4 text-left rounded-lg transition-colors duration-200 ${
              selectedAnswer === option
                ? isCorrect === true
                  ? 'bg-green-100 border-2 border-green-500'
                  : isCorrect === false
                  ? 'bg-red-100 border-2 border-red-500'
                  : 'bg-purple-100 border-2 border-purple-500'
                : 'bg-gray-50 hover:bg-purple-50 border-2 border-transparent'
            }`}
            disabled={selectedAnswer !== null}
          >
            {option}
            {selectedAnswer === option && isCorrect !== null && (
              <span className="float-right">
                {isCorrect ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuizSection; 